/**
 * Merchant-couriers link / unlink hooks.
 *
 * Endpoints under `/merchants/{id}/couriers` are not in the OpenAPI build —
 * `as never` casts apply throughout.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';
import { merchantOpsKeys, unwrap } from './keys';

export type CourierResponse = components['schemas']['CourierResponse'];

export function useMerchantCouriers(
  merchantId: string | null,
): UseQueryResult<CourierResponse[], Error> {
  return useQuery<CourierResponse[], Error>({
    queryKey: merchantOpsKeys.couriers(merchantId ?? 'none'),
    enabled: merchantId !== null,
    queryFn: async () => {
      if (!merchantId) throw new Error('Selecione um estabelecimento');
      const { data, error } = await api.GET(
        '/api/v1/merchants/{id}/couriers' as never,
        { params: { path: { id: merchantId } } } as never,
      );
      return unwrap(data as CourierResponse[] | undefined, error);
    },
  });
}

interface CourierLinkVars {
  merchantId: string;
  courierId: string;
}

export function useLinkCourier(): UseMutationResult<void, Error, CourierLinkVars> {
  const qc = useQueryClient();
  return useMutation<void, Error, CourierLinkVars>({
    mutationFn: async ({ merchantId, courierId }) => {
      const { error } = await api.POST(
        '/api/v1/merchants/{id}/couriers' as never,
        { params: { path: { id: merchantId } }, body: { courierId } } as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}

export function useUnlinkCourier(): UseMutationResult<void, Error, CourierLinkVars> {
  const qc = useQueryClient();
  return useMutation<void, Error, CourierLinkVars>({
    mutationFn: async ({ merchantId, courierId }) => {
      const { error } = await api.DELETE(
        '/api/v1/merchants/{id}/couriers/{courierId}' as never,
        { params: { path: { id: merchantId, courierId } } } as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}
